/**
 * AudioManager - Handles background music streaming from Streamon.fm
 */

export class AudioManager {
  private audio: HTMLAudioElement | null = null;
  private isPlaying: boolean = false;
  private isMuted: boolean = false;
  private volume: number = 0.3;

  constructor() {
    this.setupAudio();
  }

  private setupAudio(): void {
    // Create audio element for Streamon.fm 80s music stream
    this.audio = new Audio('https://ais-sa1.streamon.fm/7969_96k.aac');
    this.audio.loop = true;
    this.audio.preload = 'none';
    this.audio.volume = this.volume;

    // Event listeners
    this.audio.addEventListener('loadstart', () => {
      console.log('🎵 Loading 80s music stream...');
    });

    this.audio.addEventListener('canplay', () => {
      console.log('🎵 Music stream ready to play');
    });

    this.audio.addEventListener('error', (e) => {
      console.error('🚫 Music stream error:', e);
    });

    this.audio.addEventListener('playing', () => {
      console.log('🎵 Music playing');
      this.isPlaying = true;
    });

    this.audio.addEventListener('pause', () => {
      console.log('⏸️ Music paused');
      this.isPlaying = false;
    });
  }

  public async play(): Promise<void> {
    if (!this.audio) return;

    try {
      await this.audio.play();
      this.isPlaying = true;
      console.log('🎵 Started 80s music stream');
    } catch (error) {
      console.error('Failed to play music:', error);
      // Browsers may block autoplay - user needs to click first
    }
  }

  public pause(): void {
    if (this.audio) {
      this.audio.pause();
      this.isPlaying = false;
    }
  }

  public async toggle(): Promise<boolean> {
    if (this.isPlaying) {
      this.pause();
      return false;
    } else {
      try {
        await this.play();
        return true;
      } catch (error) {
        console.error('Failed to start music:', error);
        return false;
      }
    }
  }

  public setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    if (this.audio) {
      this.audio.volume = this.volume;
    }
  }

  public getVolume(): number {
    return this.volume;
  }

  public isStreamPlaying(): boolean {
    return this.isPlaying;
  }

  public mute(): void {
    if (this.audio) {
      this.audio.muted = true;
      this.isMuted = true;
    }
  }

  public unmute(): void {
    if (this.audio) {
      this.audio.muted = false;
      this.isMuted = false;
    }
  }

  public isMutedState(): boolean {
    return this.isMuted;
  }
}
